import { useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import React, { useEffect, useMemo, useState } from 'react'
import { getCoverImagePath, getEcardImagePathArr, getPlasticCardImagePath } from '../../services/utilities'
import CardImageShow from '../CertShow/CertShow'
import { Button, Swiper } from 'antd-mobile-v5'
import { useBindEcard, useBindingEcards, useCancelBindingEcards, useGetAllCardsImg } from '../../services/hooks'
import { isFaceSelectionApplied, resolveEcardFaceSelection } from '../common/utils'
import { showToast } from '../common/tools'
import { changeEcardActiveFalse, changeEcardActiveTrue } from '../../actions'

/**
 * 按索引对齐取「电子卡面」对象，不再用字符串前缀反查。
 *
 * `allImgs = [...getEcardImagePathArr(cert), plasticImgPath]`，而
 * `getEcardImagePathArr = customizedImages.map(imgUrl)` 拼接 `ecardImages.map(getEcardImagePath)`
 * ⇒ `index < customizedImages.length` 时是自定义面（返回 null），否则是
 * `ecardImages[index - customizedImages.length]`。
 *
 * CHNA-2776：旧写法 `newPath.indexOf(e.imgFile.split('.')[0]) > 0` 会跨形态误匹配——
 * 76（相对逻辑名 `year2026.jpg`）与 78（绝对 URL `…/year2026.jpg`）是同一张图、不同 id，
 * 滑到 78 时会先命中 76，于是装备/选中的是 76。
 */
export const resolveEcardImageAt = (cert, index) => {
  const customizedCount = (cert?.customizedImages || []).length
  if (index < customizedCount) return null
  return (cert?.ecardImages || [])[index - customizedCount] || null
}

const CertShowWrapped = ({ cert }) => {
  const dispatch = useDispatch()
  const currentCoverImg = getCoverImagePath(cert)
  const plasticImgPath = getPlasticCardImagePath(cert)
  const ecardImgPath = getEcardImagePathArr(cert)
  const allImgs = [...ecardImgPath, plasticImgPath]
  // 选中项的原始描述（普通版 / 自定义面 / 目录面）；权威面在渲染期由宽集补齐，故不在此算「可装备」
  const [selected, setSelected] = useState(null)
  // 防重复提交：装备/取消装备 + 回读期间按钮保持 loading
  const [submitting, setSubmitting] = useState(false)
  const activeImageIndex = allImgs.findIndex((path) => path === currentCoverImg)

  // 权威候选面取宽集（GET /api/ecards/images，与 MainlandCertShowWrapper 同源）：证书列表 cert.ecardImages
  // 是窄集、vipAvailable 恒 false，只有本接口会赋值 vipAvailable（见 isFaceEquippable 注释）。VIP 面不再物化
  // 绑定行，切走后 active=false，只有靠宽集的 vipAvailable 才能判出「仍可装备」。
  const { data: allCards, refreshAsync: reloadCards } = useGetAllCardsImg(cert.encodedId, {})
  const faceMap = useMemo(() => {
    const map = new Map()
    ;((allCards && allCards.ecardImages) || []).forEach((face) => {
      if (face && face.ecardImageId != null) map.set(face.ecardImageId, face)
    })
    return map
  }, [allCards])

  const { runAsync: rebindEcards } = useBindingEcards({})
  const { runAsync: cancelBindEcards } = useCancelBindingEcards({})
  const { runAsync: bindFace } = useBindEcard({})

  const onIndexChange = (index) => {
    const ecardId = cert.affiliateCertificationId || cert.affiliateCredentialId
    const newPath = allImgs[index]
    if (newPath === plasticImgPath) {
      //切换为标准卡
      setSelected({ ecardId, plastic: true })
    } else if (ecardImgPath.indexOf(newPath) >= 0) {
      // 切换为电子卡
      if (newPath.indexOf('ECARD_CUSTOMIZATION') > 0) {
        const bindingId = cert.customizedImages.find((item) => ecardImgPath[index] === item.imgUrl)['id']
        setSelected({ ecardId, customBindingId: bindingId })
      } else {
        // 按索引对齐取面对象：同图不同 id（76/78）时前缀反查会认错 id，见 resolveEcardImageAt 注释
        const card = resolveEcardImageAt(cert, index)
        if (card) setSelected({ ecardId, face: card })
      }
    } else {
      // 切换为自定义卡
      const bindingId = cert.customizedImages.find((item) => ecardImgPath[index] === item.imgUrl)['id']
      setSelected({ ecardId, customBindingId: bindingId })
    }
  }

  // 端点与载荷由纯函数给出：宽集权威面在此对齐（宽集异步到达后自动重算，见 useMemo 依赖 faceMap）
  const selection = useMemo(() => {
    if (!selected) return null
    const authoritativeFace = selected.face ? faceMap.get(selected.face.ecardImageId) : undefined
    return resolveEcardFaceSelection({ ...selected, authoritativeFace })
  }, [selected, faceMap])

  // 回读宽集权威状态：PUT /rebinding 只回显请求（后端对不可得面静默跳过），POST /binding 也可能被权益
  // 校验拒掉——都不能凭响应弹「成功」。判据见 isFaceSelectionApplied。
  const confirmApplied = async (item) => {
    try {
      const latest = await reloadCards()
      return isFaceSelectionApplied(latest, {
        ecardImageId: item.ecardImageId != null ? item.ecardImageId : item.ecardImgId,
        bindingId: item.bindingId,
      })
    } catch (e) {
      return false
    }
  }

  const handleBinding = async () => {
    // 端点由纯函数给出：普通版（无面）cancelBinding；目录面/自定义面 rebinding；无绑定行的换面集面
    // POST /binding（rebinding 会被后端静默跳过）。不可得的目录面纯函数返回 null（不发请求）——绝不把
    // 「装备」发成「取消装备」，否则既装不回去还会卸掉当前装备的面。
    if (!selection || submitting) return
    const [item] = selection.payload
    setSubmitting(true)
    try {
      let result
      if (selection.endpoint === 'binding') result = await bindFace(selection.payload)
      else if (selection.endpoint === 'rebinding') result = await rebindEcards(selection.payload)
      else result = await cancelBindEcards(selection.payload)
      const applied = await confirmApplied(item)
      if (!applied) {
        showToast('卡面更换失败，请稍后重试', false)
        return
      }
      showToast('卡面更换成功！')
      if (selection.endpoint === 'cancelBinding') dispatch(changeEcardActiveFalse(result))
      else dispatch(changeEcardActiveTrue(result))
    } catch (e) {
      // 权益不足（如非 VIP 的 club.vip.need）等错误已由 useRequestCommon 弹窗提示，这里不弹成功
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    onIndexChange(activeImageIndex)
  }, [])

  if (cert.customizedImages || cert.ecardImages) {
    return (
      <>
        <div style={{ fontSize: '.8rem' }}>
          <FormattedMessage id="certdetail-switchCoverImage" />
        </div>
        <Swiper
          style={{ '--track-padding': '8px 0 16px' }}
          defaultIndex={activeImageIndex}
          onIndexChange={onIndexChange}
        >
          {allImgs.map((imgPath, index) => (
            <Swiper.Item key={imgPath}>
              <CardImageShow path={imgPath} cert={cert} key={index} />
            </Swiper.Item>
          ))}
        </Swiper>
        <Button block color="primary" loading={submitting} onClick={handleBinding}>
          确定使用该卡面
        </Button>
      </>
    )
  } else {
    return <CardImageShow path={currentCoverImg} cert={cert} />
  }
}

export default CertShowWrapped
